import { pinActionColumnsFirst } from '../Components/data/dataTableTypes';
import type { NavItem } from '../data/menu';

/** Persistência de preferências do shell Nexus (localStorage por usuário). */

export type NexusLayoutMode = 'sidebar' | 'top';
export type NexusThemeMode = 'light' | 'dark' | 'system';
export type TableActionsDisplayMode = 'dropdown' | 'buttons';

export type PageColumnPreferences = {
    columns?: Record<string, boolean>;
    /** Ordem das colunas reordenáveis (exclui colunas fixas como `#` e seleção em massa). */
    columnOrder?: string[];
    /** Modo de visualização da listagem (tabela, grid ou lista). */
    viewMode?: 'table' | 'grid' | 'list';
};

export type LayoutPreferences = {
    mode: NexusLayoutMode;
    theme?: NexusThemeMode;
    /** Ordem personalizada dos IDs dos itens do menu superior (AppTopNav). */
    topNavOrder?: string[];
    /** Estilo de exibição dos botões de ação nas tabelas (avulsos ou menu dropdown 3 pontos). */
    tableActionsMode?: TableActionsDisplayMode;
    /** Preferências de colunas por página — origem no servidor (não localStorage). */
    pages?: Record<string, PageColumnPreferences | undefined>;
};

const STORAGE_PREFIX = 'nexus-layout-prefs';
export const DEFAULT_LAYOUT_PREFS: LayoutPreferences = {
    mode: 'sidebar',
    theme: 'light',
    tableActionsMode: 'buttons',
};

const listeners = new Set<() => void>();

export function emitLayoutPreferencesChange(): void {
    listeners.forEach((listener) => listener());
}

export function subscribeLayoutPreferences(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function storageKey(userId: number | string | null | undefined): string {
    return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function isLayoutMode(value: unknown): value is NexusLayoutMode {
    return value === 'sidebar' || value === 'top';
}

function isThemeMode(value: unknown): value is NexusThemeMode {
    return value === 'light' || value === 'dark' || value === 'system';
}

function isTableActionsMode(value: unknown): value is TableActionsDisplayMode {
    return value === 'dropdown' || value === 'buttons';
}

export function readLayoutPreferences(key: string): LayoutPreferences {
    if (typeof window === 'undefined') {
        return { ...DEFAULT_LAYOUT_PREFS };
    }

    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
            return { ...DEFAULT_LAYOUT_PREFS };
        }

        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') {
            return { ...DEFAULT_LAYOUT_PREFS };
        }

        const record = parsed as {
            mode?: unknown;
            theme?: unknown;
            topNavOrder?: unknown;
            tableActionsMode?: unknown;
            pages?: unknown;
        };

        if (!isLayoutMode(record.mode)) {
            return { ...DEFAULT_LAYOUT_PREFS };
        }

        const theme = isThemeMode(record.theme) ? record.theme : 'light';
        const topNavOrder = Array.isArray(record.topNavOrder)
            ? record.topNavOrder.filter((id): id is string => typeof id === 'string')
            : undefined;
        const tableActionsMode = isTableActionsMode(record.tableActionsMode)
            ? record.tableActionsMode
            : 'buttons';

        return {
            mode: record.mode,
            theme,
            topNavOrder,
            tableActionsMode,
        };
    } catch {
        return { ...DEFAULT_LAYOUT_PREFS };
    }
}

export function writeLayoutPreferences(key: string, prefs: LayoutPreferences): void {
    if (typeof window === 'undefined') {
        return;
    }

    const shellOnly: Pick<LayoutPreferences, 'mode' | 'theme' | 'topNavOrder' | 'tableActionsMode'> = {
        mode: prefs.mode,
        theme: prefs.theme,
        topNavOrder: prefs.topNavOrder,
        tableActionsMode: prefs.tableActionsMode,
    };

    try {
        window.localStorage.setItem(key, JSON.stringify(shellOnly));
    } catch {
        // quota / private mode
    }
}

/** Ordena itens do menu raiz conforme preferência salva. */
export function resolveTopNavOrder(
    menu: NavItem[],
    customOrder?: string[],
): NavItem[] {
    if (!customOrder || !customOrder.length) {
        return menu;
    }

    const itemMap = new Map<string, NavItem>();
    menu.forEach((item) => itemMap.set(item.id, item));

    const result: NavItem[] = [];

    // 1. Adiciona os itens na ordem configurada (se ainda existirem no menu)
    for (const id of customOrder) {
        const item = itemMap.get(id);
        if (item) {
            result.push(item);
            itemMap.delete(id);
        }
    }

    // 2. Adiciona quaisquer itens restantes/novos que não estavam na ordem salva
    for (const item of menu) {
        if (itemMap.has(item.id)) {
            result.push(item);
        }
    }

    return result;
}

export function buildDefaultColumnVisibility<TId extends string>(
    options: ReadonlyArray<{ id: TId; defaultVisible: boolean }>,
): Record<TId, boolean> {
    return options.reduce(
        (acc, option) => {
            acc[option.id] = option.defaultVisible;
            return acc;
        },
        {} as Record<TId, boolean>,
    );
}

export function resolveColumnVisibility<TId extends string>(
    prefs: LayoutPreferences | undefined,
    prefsKey: string,
    defaults: Record<TId, boolean>,
    validIds: readonly TId[],
): Record<TId, boolean> {
    const stored = prefs?.pages?.[prefsKey]?.columns ?? {};
    const resolved = { ...defaults };

    for (const id of validIds) {
        const visible = stored[id];
        if (typeof visible === 'boolean') {
            resolved[id] = visible;
        }
    }

    return resolved;
}

export function updatePageColumnVisibility<TId extends string>(
    current: LayoutPreferences,
    prefsKey: string,
    defaults: Record<TId, boolean>,
    validIds: readonly TId[],
    column: TId,
    visible: boolean,
): LayoutPreferences {
    const nextColumns = {
        ...resolveColumnVisibility(current, prefsKey, defaults, validIds),
        [column]: visible,
    };

    return {
        ...current,
        pages: {
            ...current.pages,
            [prefsKey]: {
                ...current.pages?.[prefsKey],
                columns: nextColumns,
            },
        },
    };
}

export function resolveColumnOrder<TId extends string>(
    prefs: LayoutPreferences | undefined,
    prefsKey: string,
    reorderableIds: readonly TId[],
): TId[] {
    const stored = prefs?.pages?.[prefsKey]?.columnOrder ?? [];
    const validStored = stored.filter((id): id is TId =>
        reorderableIds.includes(id as TId),
    );
    const missing = reorderableIds.filter((id) => !validStored.includes(id));

    return pinActionColumnsFirst([...validStored, ...missing], reorderableIds);
}

export function updatePageColumnOrder<TId extends string>(
    current: LayoutPreferences,
    prefsKey: string,
    reorderableIds: readonly TId[],
    order: readonly TId[],
): LayoutPreferences {
    const validSet = new Set(reorderableIds);
    const sanitized = order.filter((id) => validSet.has(id));
    const missing = reorderableIds.filter((id) => !sanitized.includes(id));
    const nextOrder = pinActionColumnsFirst([...sanitized, ...missing], reorderableIds);
    const pagePrefs = current.pages?.[prefsKey] ?? {};

    return {
        ...current,
        pages: {
            ...current.pages,
            [prefsKey]: {
                ...pagePrefs,
                columnOrder: [...nextOrder],
            },
        },
    };
}

export function resetPageColumnVisibility<TId extends string>(
    current: LayoutPreferences,
    prefsKey: string,
    defaults: Record<TId, boolean>,
): LayoutPreferences {
    const { columnOrder: _removed, ...restPagePrefs } = current.pages?.[prefsKey] ?? {};

    return {
        ...current,
        pages: {
            ...current.pages,
            [prefsKey]: {
                ...restPagePrefs,
                columns: { ...defaults },
            },
        },
    };
}
